import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { FormField } from "@/shared/ui/form-field";
import { Checkbox } from "@/shared/ui/checkbox";
import { Label } from "@/shared/ui/label";
import { Badge } from "@/shared/ui/badge";
import { Search, Plus } from "lucide-react";
import { useLanguage } from "@/shared/hooks/use-language";
import { ScopeContextStorage } from "@/shared/lib/scope-storage.ts";
import { parseBackendErrors } from "@/shared/lib/backend-errors";
import {
  UNIQUE_ID_MAX_LENGTH,
  deriveUniqueId,
  uniqueIdErrorKey,
  uniqueIdErrorMessage,
} from "@/shared/lib/unique-id";
import type { ScopeContext } from "@/features/auth";
import { getInvestors, createInvestor, type Investor } from "@/features/auth/api/investor.ts";
import { isUnreadableResponseError } from "@/shared/lib/response-shape";
import { DebugLogger } from "@/shared/lib/debug.ts";
import { InvestorCardSkeleton } from "@/shared/components/InvestorCardSkeleton";
import { useToast } from "@/shared/hooks/use-toast";

const NEW_INVESTOR_FIELD_MAP: Record<string, string> = {
  "investors.tax_id": "taxId",
  "investors.name": "name",
  "portfolios.name": "portfolioName",
  "portfolios.unique_id": "portfolioUniqueId",
};
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

const Investors = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionId = location.state?.sessionId || ScopeContextStorage.getCurrentSessionId();
  const passedScopeContext = location.state?.scopeContext;
  
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // New investor modal state
  const [showNewInvestorModal, setShowNewInvestorModal] = useState(false);
  const [investorName, setInvestorName] = useState("");
  const [investorTaxId, setInvestorTaxId] = useState("");
  const [createPortfolio, setCreatePortfolio] = useState(false);
  const [portfolioName, setPortfolioName] = useState("");
  const [portfolioNameEdited, setPortfolioNameEdited] = useState(false);
  const [portfolioUniqueId, setPortfolioUniqueId] = useState("");
  const [portfolioUniqueIdEdited, setPortfolioUniqueIdEdited] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  const { toast } = useToast();

  useEffect(() => {
    DebugLogger.debug('Investors page mounted', { 
      sessionId, 
      passedScopeContext,
      locationState: location.state 
    });
    
    if (!sessionId) {
      DebugLogger.error('No session ID found', { 
        locationState: location.state,
        pathname: location.pathname 
      });
      setError('No session ID found. Please log in again.');
      setIsLoading(false);
      return;
    }

    // Get scope context from storage or passed state
    const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId);
    
    if (!contextToUse) {
      DebugLogger.error('No scope context found', { sessionId });
      setError('No scope context found. Please select an account.');
      setIsLoading(false);
      return;
    }

    // Ensure we have an account (investor endpoint requires account-level scope)
    if (!contextToUse.account) {
      DebugLogger.error('Incomplete scope context - no account', { contextToUse });
      setError('Incomplete scope context. Please select an account.');
      setIsLoading(false);
      return;
    }

    fetchInvestors(contextToUse, sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, passedScopeContext]);

  const fetchInvestors = async (context: ScopeContext, sessionId: string) => {
    if (!context.account) {
      setError('Account is required to fetch investors');
      setIsLoading(false);
      return;
    }

    setError(null);

    try {
      setIsLoading(true);
      const accountScopeContext: ScopeContext = {
        scopeId: context.account.id,
        scopeType: 'account'
      };
      
      DebugLogger.debug('Fetching investors', { 
        accountId: context.account.id 
      });
      
      const investorsResponse = await getInvestors(sessionId, accountScopeContext);
      setInvestors(investorsResponse.investors || []);
    } catch (err) {
      DebugLogger.error('Failed to fetch investors', { 
        error: err instanceof Error ? err.message : 'Unknown error' 
      });
      setError(
        isUnreadableResponseError(err) || !(err instanceof Error)
          ? t("errors.unreadableResponse")
          : err.message,
      );
      setInvestors([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterInvestors = (investorsList: Investor[]) => {
    if (!searchQuery) return investorsList;
    const query = searchQuery.toLowerCase();
    return investorsList.filter(investor => 
      investor.name.toLowerCase().includes(query) ||
      investor.tax_id.toLowerCase().includes(query) ||
      investor.id.toLowerCase().includes(query) ||
      investor.tags.some(tag => tag.toLowerCase().includes(query))
    );
  };

  const filteredInvestors = filterInvestors(investors);

  const resetNewInvestorForm = () => {
    setInvestorName("");
    setInvestorTaxId("");
    setCreatePortfolio(false);
    setPortfolioName("");
    setPortfolioNameEdited(false);
    setPortfolioUniqueId("");
    setPortfolioUniqueIdEdited(false);
    setFieldErrors({});
  };

  const handleCreateInvestor = async () => {
    const trimmedName = investorName.trim();
    const trimmedTaxId = investorTaxId.trim();
    const trimmedPortfolioName = portfolioName.trim();
    const trimmedPortfolioUniqueId = portfolioUniqueId.trim();

    const nextFieldErrors: Record<string, string> = {};
    if (!trimmedName) nextFieldErrors.name = t("investors.nameRequired");
    if (!trimmedTaxId) nextFieldErrors.taxId = t("investors.taxIdRequired");
    if (createPortfolio) {
      if (!trimmedPortfolioName) {
        nextFieldErrors.portfolioName = t("investors.portfolioNameRequired");
      }
      const uniqueIdError = uniqueIdErrorMessage(t, uniqueIdErrorKey(trimmedPortfolioUniqueId), "portfolio.uniqueIdRequired");
      if (uniqueIdError) nextFieldErrors.portfolioUniqueId = uniqueIdError;
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }
    setFieldErrors({});

    const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId || '');
    if (!contextToUse || !contextToUse.account) {
      toast({
        title: t("investors.error"),
        description: t("investors.noScopeContext"),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCreating(true);

      const accountScopeContext: ScopeContext = {
        scopeId: contextToUse.account.id,
        scopeType: 'account'
      };

      await createInvestor(
        sessionId || '',
        {
          investors: [
            {
              name: trimmedName,
              tax_id: trimmedTaxId,
              ...(createPortfolio
                ? {
                    portfolios: [
                      { name: trimmedPortfolioName, unique_id: trimmedPortfolioUniqueId },
                    ],
                  }
                : {})
            }
          ]
        },
        accountScopeContext
      );

      toast({
        title: t("investors.investorCreated"),
        description: createPortfolio
          ? `${t("investors.investorCreatedDesc")} ${t("investors.portfolioCreatedDesc").replace("{name}", trimmedPortfolioName)}`
          : t("investors.investorCreatedDesc"),
      });

      resetNewInvestorForm();
      setShowNewInvestorModal(false);

      fetchInvestors(contextToUse, sessionId || '');
    } catch (err) {
      DebugLogger.error('Failed to create investor', {
        error: err instanceof Error ? err.message : 'Unknown error'
      });

      const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(
        err,
        NEW_INVESTOR_FIELD_MAP,
      );

      if (Object.keys(backendFieldErrors).length > 0) {
        setFieldErrors(backendFieldErrors);
      }
      if (generalError || Object.keys(backendFieldErrors).length === 0) {
        toast({
          title: t("investors.error"),
          description: generalError || (err instanceof Error ? err.message : 'Failed to create investor'),
          variant: "destructive",
        });
      }
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <main className="flex-1 p-4 md:p-8 animate-fadeIn">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {t("investors.title")}
                </h1>
                <p className="text-muted-foreground">
                  {t("investors.subtitle")}
                </p>
              </div>
            </div>

            <div className="flex gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder={t("investors.search")}
                  className="pl-10 bg-card border-border"
                  disabled
                />
              </div>
            </div>

            <div className="space-y-4">
              <InvestorCardSkeleton />
            </div>
          </div>
        </main>
    );
  }

  if (error) {
    return (
      <main className="flex-1 p-4 md:p-8 animate-fadeIn">
          <div className="max-w-7xl mx-auto">
            <Card className="bg-destructive/10 border-destructive/20 p-6">
              <p className="text-destructive">
                <strong>{t("investors.error")}</strong> {error}
              </p>
              <Button 
                onClick={() => navigate('/console')}
                className="mt-4"
              >
                {t("investors.backToConsole")}
              </Button>
            </Card>
          </div>
        </main>
    );
  }

  return (
    <>
    <main className="flex-1 p-4 md:p-8 animate-fadeIn">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("investors.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("investors.subtitle")}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => {
                  resetNewInvestorForm();
                  setShowNewInvestorModal(true);
                }}
                className="bg-primary hover:bg-primary/90 gap-2"
                data-testid="investor-new"
              >
                <Plus className="w-4 h-4" />
                {t("investors.newInvestor")}
              </Button>
            </div>
          </div>

          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder={t("investors.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-card border-border"
              />
            </div>
          </div>

          {filteredInvestors.length === 0 ? (
            <Card className="bg-card border-border p-6" data-testid="investors-empty">
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  {searchQuery 
                    ? t("investors.noResults")
                    : t("investors.noInvestors")
                  }
                </p>
              </div>
            </Card>
          ) : (
            <div className="space-y-4 animate-fadeIn">
              {filteredInvestors.map((investor) => (
                <InvestorCard 
                  key={investor.id} 
                  investor={investor}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* New Investor Dialog */}
      <Dialog
        open={showNewInvestorModal}
        onOpenChange={(open) => {
          // A create in flight owns the dialog: dismissing it would strand the
          // outcome on a closed dialog and leave its errors for the next open.
          if (!open && isCreating) return;
          resetNewInvestorForm();
          setShowNewInvestorModal(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("investors.newInvestor")}</DialogTitle>
            <DialogDescription>
              {t("investors.newInvestorDesc")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <FormField
              id="investorName"
              label={t("investors.name")}
              placeholder={t("investors.namePlaceholder")}
              value={investorName}
              onChange={(value) => {
                setInvestorName(value);
                if (createPortfolio) {
                  const seedName = portfolioNameEdited ? portfolioName : value.trim();
                  if (!portfolioNameEdited) {
                    setPortfolioName(value);
                    setFieldErrors((prev) => ({ ...prev, portfolioName: "" }));
                  }
                  if (!portfolioUniqueIdEdited) {
                    const { derived, errorKey } = deriveUniqueId(seedName);
                    setPortfolioUniqueId(derived);
                    setFieldErrors((prev) => ({ ...prev, portfolioUniqueId: uniqueIdErrorMessage(t, errorKey, "portfolio.uniqueIdRequired") }));
                  }
                }
                if (fieldErrors.name) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.name;
                    return next;
                  });
                }
              }}
              error={fieldErrors.name}
              disabled={isCreating}
            />

            <FormField
              id="investorTaxId"
              label={t("investors.taxId")}
              placeholder={t("investors.taxIdPlaceholder")}
              value={investorTaxId}
              onChange={(value) => {
                setInvestorTaxId(value);
                if (fieldErrors.taxId) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.taxId;
                    return next;
                  });
                }
              }}
              error={fieldErrors.taxId}
              hint={t("investors.taxIdHint")}
              disabled={isCreating}
            />

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="createPortfolio"
                  checked={createPortfolio}
                  onCheckedChange={(checked) => {
                    const isChecked = checked === true;
                    setCreatePortfolio(isChecked);
                    if (isChecked) {
                      const seedName = portfolioNameEdited ? portfolioName : investorName.trim();
                      if (!portfolioNameEdited) {
                        setPortfolioName(seedName);
                        setFieldErrors((prev) => ({ ...prev, portfolioName: "" }));
                      }
                      if (!portfolioUniqueIdEdited) {
                        const { derived, errorKey } = deriveUniqueId(seedName);
                        setPortfolioUniqueId(derived);
                        setFieldErrors((prev) => ({ ...prev, portfolioUniqueId: uniqueIdErrorMessage(t, errorKey, "portfolio.uniqueIdRequired") }));
                      }
                    }
                    if (!isChecked) {
                      setFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.portfolioName;
                        delete next.portfolioUniqueId;
                        return next;
                      });
                    }
                  }}
                  disabled={isCreating}
                  data-testid="investor-create-portfolio"
                />
                <Label
                  htmlFor="createPortfolio"
                  className="text-sm font-normal leading-snug cursor-pointer"
                >
                  {t("investors.createPortfolio")}
                </Label>
              </div>

              {createPortfolio && (
                <>
                  <FormField
                    id="portfolioName"
                    label={t("investors.portfolioName")}
                    placeholder={t("investors.portfolioNamePlaceholder")}
                    value={portfolioName}
                    data-testid="investor-portfolio-name"
                    onChange={(value) => {
                      setPortfolioName(value);
                      setPortfolioNameEdited(true);
                      if (!portfolioUniqueIdEdited) {
                        const { derived, errorKey } = deriveUniqueId(value);
                        setPortfolioUniqueId(derived);
                        setFieldErrors((prev) => ({ ...prev, portfolioUniqueId: uniqueIdErrorMessage(t, errorKey, "portfolio.uniqueIdRequired") }));
                      }
                      if (fieldErrors.portfolioName) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.portfolioName;
                          return next;
                        });
                      }
                    }}
                    error={fieldErrors.portfolioName}
                    disabled={isCreating}
                  />

                  <FormField
                    id="portfolioUniqueId"
                    label={t("portfolio.uniqueIdLabel")}
                    placeholder={t("uniqueId.placeholder")}
                    hint={t("uniqueId.hint")}
                    value={portfolioUniqueId}
                    maxLength={UNIQUE_ID_MAX_LENGTH}
                    data-testid="investor-portfolio-unique-id"
                    onChange={(value) => {
                      setPortfolioUniqueId(value);
                      setPortfolioUniqueIdEdited(value.length > 0);
                      if (fieldErrors.portfolioUniqueId) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.portfolioUniqueId;
                          return next;
                        });
                      }
                    }}
                    error={fieldErrors.portfolioUniqueId}
                    disabled={isCreating}
                  />
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowNewInvestorModal(false);
                resetNewInvestorForm();
              }}
              disabled={isCreating}
            >
              {t("investors.cancel")}
            </Button>
            <Button
              onClick={handleCreateInvestor}
              disabled={isCreating}
              className="bg-primary hover:bg-primary/90"
              data-testid="investor-create-submit"
            >
              {isCreating ? t("investors.creating") : t("investors.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface InvestorCardProps {
  investor: Investor;
}

const InvestorCard = ({ investor }: InvestorCardProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const displayInitial = investor.name.charAt(0).toUpperCase();

  return (
    <Card className="bg-card border-border p-6 card-hover">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <span className="text-lg font-semibold text-primary">
              {displayInitial}
            </span>
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {investor.name}
            </h3>
            <p className="text-sm text-muted-foreground">
              {investor.tax_id}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              {t("investors.status")}
            </p>
            <Badge
              variant={investor.status === "active" ? "default" : "secondary"}
              className={
                investor.status === "active"
                  ? "bg-green-500/10 text-green-500"
                  : investor.status === "inactive"
                  ? "bg-gray-500/10 text-gray-500"
                  : "bg-yellow-500/10 text-yellow-500"
              }
            >
              {investor.status === "active" 
                ? t("investors.active") 
                : investor.status === "inactive"
                ? t("investors.inactive")
                : t("investors.pending")
              }
            </Badge>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">
              {t("investors.balance")}
            </p>
            <p className="text-lg font-semibold text-muted-foreground">
              {t("investors.empty")}
            </p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">
              {t("investors.transactions")}
            </p>
            <p className="text-lg font-semibold text-muted-foreground">
              {t("investors.empty")}
            </p>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/console/investors/${investor.id}`)}
            className="border-border hover:bg-primary hover:text-white btn-arrow"
            data-testid="investor-view-details"
          >
            {t("investors.viewDetails")}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default Investors;

